import React, { useState, useEffect } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import {
    Flex,
    Box,
    Text,
} from "@chakra-ui/react";
import {
    EllipsisVertical,
    Pencil,
    MessageSquare,
    EyeIcon,
} from "lucide-react";
import ViewVehicle from "./components/ViewVehicle";
import EditVehicleDrawer from "./components/EditVehicleDrawer";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchVehiclesAction } from "../../../state/actions/vehicleActions";
import { formatDate } from "utils/dateFormatter";
import { useCustomNotification } from "components/Toasts/OpenNotification.js";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

const VehicleManagement = () => {
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isViewVehicleDrawerOpen, setViewVehicleDrawerOpen] =
        useState(false);
    const [isEditVehicleDrawerOpen, setEditVehicleDrawerOpen] =
        useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 100;

    const history = useHistory();
    const showToast = useCustomNotification();
    const dispatch = useDispatch();

    const {
        vehicles,
        loading,
        error,
        hasMore,
        total,
    } = useSelector((state) => state.vehicles);

    useEffect(() => {
        dispatch(
            fetchVehiclesAction(
                currentPage,
                PAGE_SIZE
            )
        );
    }, [dispatch, currentPage]);

    const handleViewVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
        setViewVehicleDrawerOpen(true);
    };

    const handleCloseViewVehicleDrawer = () => {
        setSelectedVehicle(null);
        setViewVehicleDrawerOpen(false);
    };

    const handleOpenEditVehicleDrawer = (vehicle) => {
        setSelectedVehicle(vehicle);
        setEditVehicleDrawerOpen(true);
    };

    const handleCloseEditVehicleDrawer = () => {
        setSelectedVehicle(null);
        setEditVehicleDrawerOpen(false);
    };

    const generateMenuItems = (row) => {
        return [
            {
                icon: <EyeIcon size={16} />,
                text: "View Vehicle",
                onClick: () =>
                    handleViewVehicle(
                        row.original
                    ),
            },
            {
                icon: (
                    <MessageSquare size={16} />
                ),
                text: "Chat with Owner",
                onClick: () => {
                    history.push(
                        `/admin/communications?userId=${row.original?.user_info?.user_id}`
                    );
                },
            },
            {
                icon: <Pencil size={16} />,
                text: "Update Vehicle Status",
                onClick: () =>
                    handleOpenEditVehicleDrawer(
                        row.original
                    ),
            },
        ];
    };

    const columnsData = [
        {
            Header: "Owner",
            accessor: "user_info",
            Cell: ({ value }) => {
                const fullName =
                    value?.first_name &&
                    value?.last_name
                        ? `${value.first_name} ${value.last_name}`
                        : value?.username ||
                          "No owner information";

                return (
                    <Text>{fullName}</Text>
                );
            },
        },
        {
            Header: "Vehicle Model",
            accessor:
                "vehicle.vehicle_model",
        },
        {
            Header: "License Plate",
            accessor:
                "vehicle.registration_number",
        },
        {
            Header: "Type",
            accessor:
                "vehicle.vehicle_make",
        },
        {
            Header: "Status",
            accessor: (row) =>
                row.vehicle?.status ||
                "Unknown",
            Cell: ({ value }) => (
                <StatusBadge
                    label={value}
                    variant={getStatusVariant(
                        value
                    )}
                />
            ),
        },
        {
            Header: "Location",
            accessor:
                "user_info.village",
        },
        {
            Header: "Date Added",
            accessor:
                "vehicle.created_at",
            Cell: ({ value }) =>
                formatDate(value),
        },
        {
            Header: "Actions",
            accessor: "moreOptions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={
                        <EllipsisVertical
                            size={20}
                            color="grey"
                        />
                    }
                    items={generateMenuItems(
                        row
                    )}
                />
            ),
        },
    ];

    return (
        <Flex
            flexDirection="column"
            pt={{
                base: "120px",
                md: "25px",
            }}
        >
            <Box p={6}>
                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Text
                        fontSize="md"
                        fontWeight="bold"
                    >
                        {loading ? (
                            <Skeleton
                                height="20px"
                                width="100px"
                            />
                        ) : (
                            `All Vehicles: ${
                                total ??
                                vehicles?.total ??
                                0
                            }`
                        )}
                    </Text>
                </Flex>
            </Box>

            <Box
                height="70vh"
                overflowY="auto"
            >
                <ReusableTable
                    columnsData={
                        columnsData
                    }
                    tableData={
                        Array.isArray(
                            vehicles?.data
                        )
                            ? vehicles.data
                            : []
                    }
                    isLoading={loading}
                    error={error}
                    emptyMessage="No vehicles found."
                />

                <Box
                    mt={4}
                    display="flex"
                    justifyContent="center"
                    gap={2}
                >
                    <GradientButton
                        onClick={() =>
                            setCurrentPage(
                                (prev) =>
                                    Math.max(
                                        prev - 1,
                                        1
                                    )
                            )
                        }
                        disabled={
                            currentPage ===
                            1
                        }
                        height="30px"
                    >
                        Previous
                    </GradientButton>

                    <Text
                        alignSelf="center"
                    >
                        Page {
                            currentPage
                        }
                    </Text>

                    <GradientButton
                        onClick={() =>
                            setCurrentPage(
                                (prev) =>
                                    prev + 1
                            )
                        }
                        disabled={
                            !hasMore
                        }
                        height="30px"
                    >
                        Next
                    </GradientButton>
                </Box>
            </Box>

            <ReusableDrawer
                isOpen={
                    isViewVehicleDrawerOpen
                }
                onClose={
                    handleCloseViewVehicleDrawer
                }
                headerContent={
                    <h2
                        style={{
                            fontSize:
                                "md",
                        }}
                    >
                        View Vehicle
                    </h2>
                }
                bodyContent={
                    selectedVehicle && (
                        <ViewVehicle
                            vehicle={
                                selectedVehicle
                            }
                            onClose={
                                handleCloseViewVehicleDrawer
                            }
                        />
                    )
                }
            />

            <EditVehicleDrawer
                isOpen={
                    isEditVehicleDrawerOpen
                }
                onClose={
                    handleCloseEditVehicleDrawer
                }
                vehicle={
                    selectedVehicle
                }
            />
        </Flex>
    );
};

export default VehicleManagement;